import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, Clock, MapPin, CheckCircle2, ArrowUpRight, Facebook, Instagram } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Motion";
import { API, PORTAL_URL, WHATSAPP_URL, PHONE, PHONE_TEL, EMAIL, SOCIAL_LINKS } from "@/lib/site";

const inputCls = "w-full border border-line bg-surface px-4 py-3.5 text-sm text-neutral-100 outline-none placeholder:text-neutral-600 focus:border-regal-amber transition-colors";

const INFO = [
  [Phone, "Telephone", PHONE, PHONE_TEL, "contact-phone"],
  [Mail, "Email", EMAIL, `mailto:${EMAIL}`, "contact-email"],
  [Clock, "Opening Hours", "Mon–Fri 9:00–17:30 GST (UTC+4)", null, "contact-hours"],
  [MapPin, "Location", "United Arab Emirates — worldwide service", null, "contact-location"],
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [params] = useSearchParams();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Pre-fill when arriving from a "Request a Quote" CTA (e.g. Tuning Database).
  useEffect(() => {
    const vehicle = params.get("vehicle");
    const subject = params.get("subject");
    if (vehicle || subject) {
      setForm((f) => ({
        ...f,
        subject: f.subject || subject || `Remap quote — ${vehicle}`,
        message: f.message || (vehicle ? `Vehicle: ${vehicle}\n\nPlease quote me for a remap on this vehicle.` : f.message),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent", { description: "We'll reply within one working day." });
    } catch {
      toast.error("Something went wrong", { description: "Please try again or email us directly." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-36 pb-24 sm:pt-44">
      <div className="container-regal">
        <MaskedLines
          lines={["Contact Regal Tuning"]}
          className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[1.0] tracking-tight max-w-4xl"
        />
        <Reveal delay={0.4} className="mt-6 max-w-2xl">
          <p className="text-base sm:text-lg text-neutral-300 leading-relaxed">
            Questions about tools, becoming a dealer or our file service — the team is here to help.
          </p>
        </Reveal>

        <Reveal delay={0.5} className="mt-8 border border-regal-amber/40 bg-card p-6 sm:p-7" data-testid="contact-portal-notice">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-regal-amber">ECU file uploads</p>
          <p className="mt-2.5 text-sm leading-relaxed text-neutral-300">
            Please do not send ECU files through this form. Existing customers should upload and manage tuning files through the File Portal.
          </p>
          <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-portal-button" className="mt-4 inline-flex items-center gap-2 bg-regal-amber px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.16em] text-void hover:bg-amber-400 transition-colors">
            Enter File Portal <ArrowUpRight size={14} />
          </a>
        </Reveal>

        <div className="mt-16 grid gap-12 lg:grid-cols-5">
          <div className="space-y-4 lg:col-span-2">
            {INFO.map(([Icon, label, value, href, testId], i) => (
              <Reveal key={label} delay={i * 0.06}>
                <div className="flex items-start gap-4 border border-line bg-card p-5" data-testid={testId}>
                  <Icon size={20} className="mt-0.5 shrink-0 text-regal-amber" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">{label}</p>
                    {href ? (
                      <a href={href} className="mt-1 block text-sm font-semibold text-neutral-100 hover:text-regal-amber transition-colors">{value}</a>
                    ) : (
                      <p className="mt-1 text-sm font-semibold text-neutral-100">{value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
            <div className="flex gap-3" data-testid="contact-social-links">
              {[[Facebook, "Facebook"], [Instagram, "Instagram"]].map(([Icon, name]) => (
                <a key={name} href={SOCIAL_LINKS[name.toLowerCase()]} target="_blank" rel="noopener noreferrer" aria-label={`Regal Tuning on ${name} (opens in a new tab)`} title={`Regal Tuning on ${name}`} data-testid={`contact-social-${name.toLowerCase()}`} className="grid h-11 w-11 place-items-center border border-line text-neutral-400 hover:border-regal-amber hover:text-regal-amber focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-regal-amber transition-colors duration-300">
                  <Icon size={18} aria-hidden="true" />
                </a>
              ))}
            </div>
            {WHATSAPP_URL && (
              <Reveal delay={0.28}>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-button" className="flex items-center justify-center gap-2.5 border border-emerald-600/40 bg-emerald-600/5 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-emerald-700 hover:bg-emerald-600/10 transition-colors">
                  <MessageCircle size={17} /> WhatsApp Technical Desk
                </a>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-3">
            {done ? (
              <div className="grid h-full min-h-[320px] place-items-center border border-regal-amber/40 bg-card p-10 text-center" data-testid="contact-form-success">
                <div>
                  <CheckCircle2 size={48} className="mx-auto text-regal-amber" />
                  <p className="mt-5 font-display text-xl font-bold uppercase tracking-wide">Message sent</p>
                  <p className="mt-3 max-w-sm text-sm text-neutral-400">Thank you — we'll get back to you within one working day.</p>
                </div>
              </div>
            ) : (
              <Reveal delay={0.1}>
                <form onSubmit={submit} className="border border-line bg-card p-7 sm:p-10" data-testid="contact-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400">Name *</span>
                      <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
                    </label>
                    <label className="block">
                      <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400">Email *</span>
                      <input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400">Subject</span>
                      <input value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject-input" />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400">Message *</span>
                      <textarea required rows={6} value={form.message} onChange={set("message")} className={inputCls} data-testid="contact-message-input" />
                    </label>
                  </div>
                  <button type="submit" disabled={busy} data-testid="contact-form-submit" className="mt-7 w-full bg-regal-amber py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-void hover:bg-amber-400 transition-colors disabled:opacity-60 sm:w-auto sm:px-14">
                    {busy ? "Sending…" : "Send Message"}
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
